import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { AdvertiseLayout } from '@/components/advertise/AdvertiseLayout';
import { AdvertiseSideSummary } from '@/components/advertise/AdvertiseSideSummary';
import { AdvertiseFooterNav } from '@/components/advertise/AdvertiseFooterNav';
import { useAdvertise } from '@/store/useAdvertise';
import { useStepGuard, buildersStepValid } from '@/utils/advertiseGuards';
import { BUILDERS_STEPS } from '@/config/advertiseSteps';
import { submitLead } from '@/services/advertiseApi';
import { toast } from 'sonner';
import { ADVERTISE_BUILDERS } from '@/constants/testIds/advertise';

const Row = ({ label, value, editTo }) => (
  <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-2.5 last:border-0">
    <dt className="text-sm text-slate-500">{label}</dt>
    <dd className="flex items-center gap-2 text-right text-sm font-medium text-slate-900">
      {value || '—'}
      {editTo && <Link to={editTo} className="text-blue-600"><Pencil size={13} /></Link>}
    </dd>
  </div>
);

export default function BuildersReview() {
  const navigate = useNavigate();
  const builders = useAdvertise((s) => s.builders);
  const setField = useAdvertise((s) => s.setField);
  const reset = useAdvertise((s) => s.reset);
  useStepGuard(buildersStepValid.review(builders), '/advertise/builders');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await submitLead('builders', {
        companyName: builders.companyName, city: builders.city, state: builders.state, website: builders.website,
        communityCount: builders.communityCount, marketsServed: builders.marketsServed, homeTypes: builders.homeTypes,
        annualVolume: builders.annualVolume, firstName: builders.firstName, lastName: builders.lastName,
        email: builders.email, phone: builders.phone, zip: builders.zip, title: builders.title, slotIso: builders.slotIso,
      });
      navigate('/advertise/builders/thank-you');
      reset('builders');
    } catch (e) {
      toast.error(e?.detail || e?.message || 'Something went wrong submitting your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdvertiseLayout
      trackLabel="Community Boost"
      steps={BUILDERS_STEPS}
      currentStepId="review"
      title="Review your request"
      sidebar={<AdvertiseSideSummary items={[{ label: 'Company', value: builders.companyName }]} />}
      footer={
        <AdvertiseFooterNav
          onBack={() => navigate('/advertise/builders/schedule')}
          onNext={handleSubmit}
          nextDisabled={!builders.consent}
          loading={submitting}
          nextLabel="Submit request"
          nextTestId={ADVERTISE_BUILDERS.reviewSubmit}
        />
      }
    >
      <dl>
        <Row label="Company" value={builders.companyName} editTo="/advertise/builders/company" />
        <Row label="HQ" value={[builders.city, builders.state].filter(Boolean).join(', ')} editTo="/advertise/builders/company" />
        <Row label="Communities" value={builders.communityCount} editTo="/advertise/builders/company" />
        <Row label="Markets served" value={builders.marketsServed.join(', ')} editTo="/advertise/builders/portfolio" />
        <Row label="Home types" value={builders.homeTypes.join(', ')} editTo="/advertise/builders/portfolio" />
        <Row label="Contact" value={[builders.firstName, builders.lastName].filter(Boolean).join(' ')} editTo="/advertise/builders/contact" />
        <Row label="Email" value={builders.email} editTo="/advertise/builders/contact" />
        <Row label="Call time" value={builders.slotLabel || 'Email follow-up'} editTo="/advertise/builders/schedule" />
      </dl>

      <label className="mt-6 flex cursor-pointer items-start gap-2.5">
        <Checkbox checked={builders.consent} onCheckedChange={(v) => setField('builders', { consent: !!v })} className="mt-0.5" data-testid="builders-review-consent" />
        <span className="text-sm text-slate-600">
          I agree to be contacted about casahom advertising products and accept the <Link to="/terms" className="text-blue-600 underline">Terms</Link> and <Link to="/privacy" className="text-blue-600 underline">Privacy Policy</Link>.
        </span>
      </label>
    </AdvertiseLayout>
  );
}
